import { runtimeConfig } from '@/runtime-config';
import { sanitizeErrorResponse, simplifyError } from './errorMessage';
// An empty API base URL is intentional in local development: it keeps requests
// same-origin so Vite can proxy /api to the webapi container. Do not replace an
// empty value with localhost, because that points LAN clients at themselves.
export const API_BASE_URL = runtimeConfig.apiBaseUrl ?? '';

// Define the types for options and configuration
type FetchOptions = RequestInit;

export class FetchApiError extends Error {
  status: number;

  data: unknown;

  constructor(status: number, data: unknown) {
    super(simplifyError({ status, data }));
    this.status = status;
    this.data = sanitizeErrorResponse(data, status);
  }
}

// Global headers configuration
export const globalHeaders: Record<string, string> = {};

// Function to update global headers
export const setGlobalHeaders = (newHeaders: Record<string, string>) => {
  Object.assign(globalHeaders, newHeaders);
};

export const removeGlobalHeaders = (headerKeys: string[]) => {
  headerKeys.forEach((key) => {
    delete globalHeaders[key];
  });
};

// Main apiFetch function with interceptors and type safety
const apiFetch = async <T>(endpoint: string, options: FetchOptions = {}) => {
  const { headers, ...restOptions } = options;
  const method = restOptions.method || 'GET';
  // Set default headers, including global headers
  const config: FetchOptions = {
    credentials: 'include', // ✅ Include cookies in requests
    headers: {
      ...(method !== 'GET' && { 'Content-Type': 'application/json' }),
      // Anti-CSRF marker: a cross-site <form>/simple request cannot set a custom header
      // without a CORS preflight the API denies, so the backend trusts cookie auth on
      // state-changing requests only when this header is present.
      'X-Requested-With': 'XMLHttpRequest',
      ...globalHeaders,
      ...headers
    },
    ...restOptions
  };

  try {
    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, config);

    if (!response.ok) {
      // Server may respond with no body (404 from missing route, 405, 304, etc.).
      // Parse defensively so we surface a clean FetchApiError instead of a
      // confusing "Unexpected end of JSON input" SyntaxError.
      let data: unknown;
      const text = await response.text();
      if (!text) {
        data = { status: response.status, statusText: response.statusText };
      } else {
        try {
          data = JSON.parse(text);
        } catch {
          data = { status: response.status, statusText: response.statusText, body: text };
        }
      }
      throw new FetchApiError(response.status, data);
    }

    return response;
  } catch (error) {
    console.error('Error in apiFetch:', error);
    throw error;
  }
};

export default apiFetch;
